import type { Product } from '@gik/core/models/gik/Product';
import { CheckoutType } from '@gik/core/models/gik/Product';
import type { UIComponent } from '@gik/core/types/UI';
import bemBlock from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { ProductTile } from '@gik/ui/gik/ProductTile';
import { ProductTileToolbar } from '@gik/ui/gik/ProductTile/ProductTileToolbar';
import { Grid } from '@gik/ui/Grid';
import { FrameBone, ParagraphBone } from '@gik/ui/SkeletonLoader';
import React from 'react';

export type ProductsGridProps = {
  inkindRouteId?: string;
  userId?: string;
  skeletonItems?: number;
  products: Product[];
  cols?: number;
  emphasisTile?: React.ReactNode;
} & UIComponent;

function ProductsGridComp({
  cols = 4,
  products,
  className,
  inkindRouteId,
  emphasisTile,
}: ProductsGridProps): JSX.Element {
  const bem = bemBlock('products-grid');

  return (
    <Grid cols={cols} className={bem(null, [{ emphasis: emphasisTile }], className)}>
      {emphasisTile}
      {products?.map(product => {
        return (
          <ProductTile
            key={product.id}
            containImage={
              product.checkoutType === CheckoutType.TangoCard ||
              product.checkoutType === CheckoutType.Perfectgift ||
              product.checkoutType === CheckoutType.GiftyaPlatform
            }
            imageUrl={product.gridImage}
            toolbar={<ProductTileToolbar itemId={product.id} inkindRouteId={inkindRouteId} />}
            title={product.name}
            href={'/products/' + product.slug}
          />
        );
      })}
    </Grid>
  );
}

export const ProductsGrid = withComponentErrorBoundary(ProductsGridComp);

export const ProductsGridSkeleton = ({
  cols = 4,
  skeletonItems = 8,
}: Partial<ProductsGridProps>): React.ReactElement => {
  const skelBem = bemBlock(`products-grid-skeleton`);
  return (
    <Grid cols={cols} className={skelBem()}>
      {Array.from(Array(skeletonItems).keys()).map(index => {
        return (
          <div key={index} className={skelBem('item')}>
            <FrameBone className={skelBem('image')} />
            <main>
              <ParagraphBone words={10} className={skelBem('text')} />
            </main>
          </div>
        );
      })}
    </Grid>
  );
};
